import { useEffect, useRef, useState, type ReactNode } from "react";

import { EXPORT_FORMATS, type ExportFormat } from "../export.ts";

/**
 * The design's `gridUI()`, as components, because the design has exactly one of it.
 *
 * `20 WP19 Support Console.dc.html` renders all six of its pages from one `gridUI()` — one
 * toolbar, one empty state, one pager — and that function is byte-identical to the finance
 * console's, which was checked rather than assumed, along with `hdrUI`, `badge`, `glyph`, `_rv`
 * and `THEMEV`. This console draws four grids, and four copies of a hundred lines of toolbar is
 * four places for them to drift apart; the design's own answer is one function, so this is one
 * file.
 *
 * Design rule 4 is the specification and all of it is here: search, filter, sort, pagination with
 * rows-per-page, export to CSV, Excel and PDF, and an empty state with a Clear filters button.
 * Nothing in this file is optional and nothing in it is a screen's choice.
 *
 * What each screen still owns is its columns, its cells, its states and its closing note. Those are
 * the parts the design varies per page, and they are in the screen's own component.
 */

/** The design's own rows-per-page choices, in its order: `[5, 10, 25, 50]`. */
export const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
export const ORDER_CHOICES = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
] as const;

/** The four orders every board here offers, which are the design's own four. */
export type GridOrder = (typeof ORDER_CHOICES)[number]["value"];

export interface GridToolbarProps {
  /**
   * Prefixes this board's control identifiers, so three grids on one console never collide and a
   * fidelity check can address one by name.
   */
  readonly idPrefix: string;
  readonly search: string;
  readonly onSearch: (next: string) => void;
  /** The chosen state, or `undefined` for the design's `All states`. */
  readonly state: string | undefined;
  readonly onState: (next: string | undefined) => void;
  /**
   * The states present in what came back, in board order.
   *
   * The design's own `gridUI` builds this list from the rows rather than from a vocabulary —
   * `[...new Set(items.map(x => x.state))]` — and on all three of this console's boards that is
   * also the only thing that can be done: the state filter is applied in the browser, because
   * sending SUP-01's `status` parameter would re-fetch from a different cursor every time the
   * agent changed it. Each board says so under its grid.
   */
  readonly states: readonly string[];
  readonly order: GridOrder;
  readonly onOrder: (next: GridOrder) => void;
  /** Writes what the agent is looking at. The screen owns the columns and the file's name. */
  readonly onExport: (format: ExportFormat) => void;
}

export function GridToolbar({
  idPrefix,
  search,
  onSearch,
  state,
  onState,
  states,
  order,
  onOrder,
  onExport,
}: GridToolbarProps): ReactNode {
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an agent uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  return (
    <div className="records__toolbar">
      <span className="records__search">
        <span aria-hidden="true" className="records__search-glyph">
          search
        </span>
        <input
          id={`${idPrefix}-search`}
          type="search"
          aria-label="Search"
          placeholder="Search"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
      </span>
      <select
        id={`${idPrefix}-state`}
        aria-label="Filter by state"
        value={state ?? ""}
        onChange={(event) => onState(event.target.value === "" ? undefined : event.target.value)}
      >
        {/* The design's own word for the unfiltered option. */}
        <option value="">All states</option>
        {states.map((label) => (
          <option key={label} value={label}>
            {label}
          </option>
        ))}
      </select>
      <select
        id={`${idPrefix}-order`}
        aria-label="Sort"
        value={order}
        onChange={(event) => onOrder(event.target.value as GridOrder)}
      >
        {ORDER_CHOICES.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
      <span className="records__export" ref={exportRegion}>
        <button
          type="button"
          className="records__export-button"
          aria-expanded={exportOpen}
          onClick={() => {
            setExportOpen((open) => !open);
          }}
        >
          <span aria-hidden="true" className="records__export-glyph">
            download
          </span>
          Export
        </button>
        {exportOpen ? (
          <div className="records__export-menu" role="menu">
            {EXPORT_FORMATS.map((choice) => (
              <button
                key={choice.format}
                type="button"
                role="menuitem"
                className="records__export-item"
                onClick={() => {
                  setExportOpen(false);
                  onExport(choice.format);
                }}
              >
                <span aria-hidden="true" className="records__export-item-glyph">
                  {choice.glyph}
                </span>
                {choice.label}
              </button>
            ))}
          </div>
        ) : null}
      </span>
    </div>
  );
}

/**
 * The design's empty state: `search_off`, one sentence, and a Clear filters button.
 *
 * Its copy is the design's word for word — "Nothing matches this search or filter." — including the
 * full stop, which design rule 4 and CLAUDE.md section 1 both require be kept.
 */
export function NoMatch({ onClear }: { readonly onClear: () => void }): ReactNode {
  return (
    <div className="records__no-match">
      <span aria-hidden="true" className="records__no-match-glyph">
        search_off
      </span>
      Nothing matches this search or filter.
      <button type="button" className="records__clear" onClick={onClear}>
        Clear filters
      </button>
    </div>
  );
}

export interface GridPagerProps {
  readonly idPrefix: string;
  /** 1-based, as the design's pager shows it. */
  readonly page: number;
  readonly pageCount: number;
  readonly perPage: number;
  readonly onPerPage: (next: number) => void;
  readonly total: number;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
  /**
   * Whether the service answered a cursor, so there are older rows this board has not fetched.
   *
   * This is what makes Next real rather than a client-side page flip, and it is safe on all three
   * of this console's boards for one measured reason:
   * `docs/adr/0027-a-money-listing-pages-over-an-immutable-total-ordering.md` gave each of them an
   * ordering that is both unique and immutable — a case's `id`, a timeline entry's
   * `sourceSequence`, and a `requested_at` column no transition rewrites on FIN-04. A keyset cursor over a
   * *mutable* sort key loses rows silently, which is why the tenant admin console's audit board
   * does not page at all: AUD-01's only cursor is a bare instant.
   *
   * So Next stays enabled past the last held page while a cursor is outstanding, and the screen's
   * handler fetches.
   */
  readonly more: boolean;
  /** True while a fetch started by Next is in flight, so it cannot be pressed twice. */
  readonly fetching?: boolean;
}

export function GridPager({
  idPrefix,
  page,
  pageCount,
  perPage,
  onPerPage,
  total,
  onPrevious,
  onNext,
  more,
  fetching,
}: GridPagerProps): ReactNode {
  const shown = Math.min(page, pageCount);
  return (
    <div className="records__pager">
      {/*
        The design's own range sentence, word for word. What it counts is the rows this board has
        fetched rather than the tenant's total, because no listing on this platform publishes a
        total (R-04) and these three publish a cursor instead. Each board says so in the line under
        the grid rather than by changing the design's words here.
      */}
      <span>
        {total === 0
          ? "No matching rows"
          : `Showing ${String((shown - 1) * perPage + 1)}–${String(Math.min(shown * perPage, total))} of ${String(total)}`}
      </span>
      <span className="records__pager-controls">
        <select
          id={`${idPrefix}-per-page`}
          aria-label="Rows per page"
          value={perPage}
          onChange={(event) => onPerPage(Number(event.target.value))}
        >
          {PER_PAGE_CHOICES.map((choice) => (
            <option key={choice} value={choice}>
              {choice} per page
            </option>
          ))}
        </select>
        <button type="button" aria-label="Previous page" disabled={shown <= 1} onClick={onPrevious}>
          <span aria-hidden="true">chevron_left</span>
        </button>
        <span className="records__page">
          Page {String(shown)} of {String(pageCount)}
        </span>
        <button
          type="button"
          aria-label="Next page"
          disabled={(shown >= pageCount && !more) || fetching === true}
          onClick={onNext}
        >
          <span aria-hidden="true">chevron_right</span>
        </button>
      </span>
    </div>
  );
}
